import ContextMenu from "../admin_controllers/context_menu"
import simplify from "../admin_controllers/simplify"
import localforage from "localforage"
import ShapeEditor from "../admin_controllers/shape_editor";

export const TOUR_CITY_DATA_TYPE = 1
export const SCENIC_SPOT_DATA_TYPE = 2

const DefaultPathOptions = {
    "colors": [ "#0cc2f2", "#4fd2b1", "#90e36f", "#ffe700", "#ff1800", "#ff6700" ],
    "width": 6
}

const DefaultTextOptions = { "size": "12px", "color": "#000000" }

export default class extends ApplicationController {
    static targets = ["mapWidget"]
    static values = { articles: Array }

    connect() {
        this.initMap()

        this._mapExporter = new MapExporter(this)
    }

    get map() {
        return this._map
    }

    initMap() {
        const tileLayer = new AMap.TileLayer({ opacity: 0 })

        const mapOptions = {
            zoom: 16,
            layers: [tileLayer],
            doubleClickZoom: false,
        }

        this._map = new AMap.Map(this.mapWidgetTarget, mapOptions)
        this._map.addControl(new AMap.Scale())

        this._mapContextMenu = new ContextMenu()

        this._mapContextMenu.addItem('导出为图片', this._onExport)
        this._mapContextMenu.addItem('整体设置', this._onGeneralSettings)
        this._mapContextMenu.addItem('原始路线', this._onLoadOriginalPath)

        this._map.on('rightclick', this._onMapRightClick)
        this._map.on('click', () => this._deSelectShapes())

        this._textContextMenu = new ContextMenu()
        this._textContextMenu.addItem("删除文本", this._onRemoveText)

        this._pathContextMenu = new ContextMenu()
        this._pathContextMenu.addItem("设置路线颜色", this._onPathSettings)

        this._pathTolerance = 0.01

        this._initPositionMarker()
        this._loadPaths()
        this._loadTexts()
        this._initPathEditor()
    }

    getTextFontStyle(text) {
        const dataType = text.getExtData()
        return dataType === TOUR_CITY_DATA_TYPE ? "bold" : "normal"
    }

    _loadPaths(isPathSimplified = true) {
        this._getHistoryAttribute("path", DefaultPathOptions, (value) => {
            this._pathOptions = value

            for (const index in this.articlesValue) {
                const article = this.articlesValue[index]

                const pathWidth = this._pathOptions["width"]
                const strokeColor = this._getPathColor(index)

                const polyline = new AMap.Polyline({
                    path: isPathSimplified ? simplify(article.path, this._pathTolerance, true) : article.path,
                    strokeColor: strokeColor,
                    strokeWeight: pathWidth,
                    strokeOpacity: 0.8,
                })

                polyline.setExtData(index)
                polyline.on("rightclick", this._onPathRightClick)
                polyline.on("dblclick", this._onPathDblClick)

                this._map.add(polyline)
            }
        })
    }

    _getPathColor(index) {
        const colors = this._pathOptions["colors"]
        const length = colors.length

        // If colors are insufficient, then fill up.
        if (index >= length) {
            for (let tmpIndex = length; tmpIndex <= index; tmpIndex++) {
                colors.push(colors[tmpIndex % length])
            }
        }

        return colors[index]
    }

    _loadTexts() {
        this._getHistoryAttribute("text", DefaultTextOptions, (value) => {
            this._textOptions = value

            for (const article of this.articlesValue) {
                for (const spot of article.spots) {
                    const fontSize = this._textOptions["size"]
                    const fontColor = this._textOptions["color"]

                    const dataType = spot.type

                    const text = new AMap.Text({
                        text: spot.name,
                        position: spot.pos,
                        cursor: "pointer",
                        draggable: true,
                    })

                    text.setExtData(spot.type)

                    this._setTextStyle(text)

                    text.on("rightclick", this._onTextRightClick)
                    text.on("click", () => this._selectText(text, spot.pos))

                    this._map.add(text)
                }
            }
        })
    }

    _setTextStyle(text, isSelected = false) {
        const style = {
            "background-color": "transparent",
            "font-weight": this.getTextFontStyle(text),
            "border-width": 0,
            "font-size": this._textOptions["size"],
            "color": this._textOptions["color"],
        }

        if (isSelected) {
            style["border-width"] = "2px"
            style["border-color"] = "#0000FF77"
        }

        text.setStyle(style)
    }

    _initPositionMarker() {
        this._positionMarker = new AMap.CircleMarker({
            radius: 5,
            fillColor: "#0000FF",
            strokeWeight: 2,
            strokeColor: "white",
            strokeOpacity: 0.5,
            fillOpacity: 0.5,
            zIndex: 100,
        })
    }

    _initPathEditor() {
        this._pathEditor = new ShapeEditor(AMap.PolylineEditor, this._map, null, false, false)
    }

    _closePathEditor() {
        if (this._pathEditor) {
            this._pathEditor.close()
        }
    }

    _selectText(text, position) {
        this._positionMarker.setOptions({center: position})
        this._positionMarker.setMap(this._map)

        if (this._lastSelectedText) {
            this._setTextStyle(this._lastSelectedText, false)
        }

        this._setTextStyle(text, true)
        this._lastSelectedText = text
    }

    _deSelectShapes() {
        this._positionMarker.setMap(null)

        if (this._lastSelectedText) {
            this._setTextStyle(this._lastSelectedText, false)
            this._lastSelectedText = null
        }

        this._closePathEditor()
    }

    _getHistoryAttribute(name, defaultValue, callback) {
        const storeName = this._getStoreName(name)

        localforage.getItem(storeName).then((value) => {
            if (!value) {
                value = defaultValue
            }

            callback(value)

            this._map.setFitView()
        }).catch(() => {
            console.error(`load attribute ${storeName} failed!`)
        })
    }

    _setHistoryAttribute(name, value) {
        const storeName = this._getStoreName(name)
        localforage.setItem(storeName, value)
    }

    _getStoreName(name) {
        return `self_drive_route_show:show:${name}`
    }

    _onMapRightClick = (event) => {
        this._mapContextMenu.open(this._map, event.lnglat)
    }

    _onLoadOriginalPath = (event) => {
        for (const polyline of this._map.getAllOverlays("polyline")) {
            this._map.remove(polyline)
        }

        this._loadPaths(false)

        this._mapContextMenu.removeItem("原始路线")
        this._mapContextMenu.addItem("简化路线", this._onLoadSimplifiedPath)
    }

    _onLoadSimplifiedPath = (event) => {
        for (const polyline of this._map.getAllOverlays("polyline")) {
            this._map.remove(polyline)
        }

        this._loadPaths(true)
        this._mapContextMenu.removeItem("简化路线")
        this._mapContextMenu.addItem("原始路线", this._onLoadOriginalPath)
    }

    _onGeneralSettings = (event) => {
        const oldSettings = {
            fontColor: this._textOptions["color"],
            fontSize: this._textOptions["size"],
            pathWidth: this._pathOptions["width"],
            pathTolerance: this._pathTolerance
        }

        const dialog = new GeneralSettingsDialog(oldSettings)

        dialog.show((newSettings) => {
            if (oldSettings["fontColor"] != newSettings["fontColor"] ||
                oldSettings["fontSize"] != newSettings["fontSize"]) {

                this._textOptions["color"] = newSettings["fontColor"]
                this._textOptions["size"] = newSettings["fontSize"]

                this._setHistoryAttribute("text", this._textOptions)

                for (const textOverlay of this._map.getAllOverlays("text")) {
                    this._setTextStyle(textOverlay)
                }
            }

            if (oldSettings["pathWidth"] != newSettings["pathWidth"]) {
                this._pathOptions["width"] = newSettings["pathWidth"]

                this._setHistoryAttribute("path", this._pathOptions)

                for (const polyline of this._map.getAllOverlays("polyline")) {
                    polyline.setOptions({
                        strokeWeight: this._pathOptions["width"]
                    })
                }
            }

            if (this._pathTolerance != newSettings["pathTolerance"]) {
                this._pathTolerance = newSettings["pathTolerance"]

                for (const polyline of this._map.getAllOverlays("polyline")) {
                    this._map.remove(polyline)
                }

                this._loadPaths(true)
            }

            this._map.setFitView()
        })
    }

    _onPathSettings = (event) => {
        if (this._pathTargetOnRightClicked) {
            const pathIndex = this._pathTargetOnRightClicked.getExtData()
            const oldStrokeColor = this._pathTargetOnRightClicked.getOptions().strokeColor

            const dialog = new PathSettingsDialog(oldStrokeColor)
            dialog.show((newStrokeColor) => {
                if (oldStrokeColor != newStrokeColor) {
                    const colors = this._pathOptions["colors"]
                    colors[pathIndex] = newStrokeColor

                    this._pathTargetOnRightClicked.setOptions({
                        strokeColor: newStrokeColor
                    })

                    this._setHistoryAttribute("path", this._pathOptions)

                    this._pathTargetOnRightClicked = null

                    this._map.setFitView()
                }
            })
        }
    }

    _onPathRightClick = (event) => {
        this._pathTargetOnRightClicked = event.target
        this._pathContextMenu.open(this._map, event.lnglat)
    }

    _onPathDblClick = (event) => {
        const target = event.target
        this._pathEditor.select(target)
    }

    _onExport = (event) => {
        this._mapExporter.export(this.mapWidgetTarget.clientWidth,
            this.mapWidgetTarget.clientHeight)
    }


    _onTextRightClick = (event) => {
        this._textTargetOnRightClicked = event.target
        this._textContextMenu.open(this._map, event.lnglat)
    }

    _onRemoveText = (event) => {
        if (this._textTargetOnRightClicked) {
            if (confirm("确认要删除该文本吗?")) {
                this._map.remove(this._textTargetOnRightClicked)
            }
            this._textTargetOnRightClicked = null
        }
    }
}

class MapExporter {
    constructor(controller) {
        this._controller = controller
        this._map = controller.map
    }

    export(width, height) {
        const container = document.createElement("div")
        document.body.appendChild(container)

        const stage = this._render(container, width, height)

        const dataURL = stage.toDataURL({pixelRatio: 2})

        const link = document.createElement('a')
        link.download = `self_drive_route_show_${Date.now()}`
        link.href = dataURL

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        document.body.removeChild(container)
    }

    _render(container, width, height) {
        const stage = new Konva.Stage({
            container: container,
            width: width,
            height: height,
        })

        const layer = new Konva.Layer()

        this._renderPath(layer)
        this._renderText(layer)

        stage.add(layer)

        return stage
    }

    _renderPath(layer) {
        for (const polyline of this._map.getAllOverlays("polyline")) {
            const polylinePath = polyline.getPath().map((lnglat) => [lnglat.lng, lnglat.lat])

            const options = polyline.getOptions()
            const strokeColor = options.strokeColor
            const strokeWidth = options.strokeWeight

            const path = new Konva.Path({
                data: this._convertToSvgPath(polylinePath),
                stroke: strokeColor,
                strokeWidth: strokeWidth
            })

            layer.add(path)
        }
    }

    _renderText(layer) {
        for (const textOverlay of this._map.getAllOverlays("text")) {
            const { x, y } = this._map.lngLatToContainer(textOverlay.getPosition())
            const style = textOverlay.getOptions().style

            const fontSize = parseInt(style["font-size"])
            const fontColor = style["color"]

            const text = new Konva.Text({
                x: x,
                y: y,
                text: textOverlay.getText(),
                fontSize: fontSize,
                fill: fontColor,
                fontStyle: this._controller.getTextFontStyle(textOverlay)
            })
            layer.add(text)
        }
    }

    _convertToSvgPath(path) {
        if (path.length <= 0) {
            return ""
        }

        const coordinates = path.map((point) => {
            const {x, y} = this._map.lngLatToContainer(point)
            return [Math.round(x), Math.round(y)]
        })

        const svgPath = coordinates.map(([x, y]) => `L${x},${y}`).join(" ")
        return "M" + svgPath.slice(1)
    }
}

class SettingsDialog {
    show(callback) {
        document.body.insertAdjacentHTML("beforeend",
            this._getDialogTemplate())

        this._dialog = document.querySelector("#settings-dialog")
        this._callback = callback

        this._bindDialogEvents()

        this._openModal()
    }

    _bindDialogEvents() {
        (this._dialog.querySelectorAll('.modal-background, .modal-card-head .delete, .modal-card-foot .button') || []).forEach((close) => {
            close.addEventListener('click', () => {
                this._closeModal()
            })
        })
    }

    _openModal() {
        this._dialog.classList.add("is-active")
    }

    _closeModal() {
        this._dialog.classList.remove("is-active")
        this._dialog.remove()
    }

    _checkInputColorValue(input) {
        if (!/^[0-9A-Fa-f]{6}$/.test(input.value)) {
            input.focus()
            alert("请输入一个有效的颜色值(值为6个十六进制数)")
            return false
        }

        return true
    }

    _checkInputIntValue(input) {
        if (!/^\d+$/.test(input.value)) {
            input.focus()
            alert("请输入一个有效的整数值")
            return false
        }

        const value = parseInt(input.value)
        if (value === 0) {
            input.focus()
            alert("请输入一个大于0的整数值")
            return false
        }

        return true
    }

    _checkInputFloatValue(input) {
        if (!/^\d+\.\d+$/.test(input.value)) {
            input.focus()
            alert("请输入一个有效的小数值, 范围为: 0.001到1.0")
            return false
        }

        const value = parseFloat(input.value)
        if (value < 0.001 || value > 1.0) {
            input.focus()
            alert("请输入一个有效的小数值, 范围为: 0.001到1.0")
            return false
        }

        return true
    }

    _getDialogTemplate() {
        return `
        <div class="modal" id="settings-dialog">
          <div class="modal-background"></div>
          <div class="modal-card">
            <section class="modal-card-body">
              ${this._getModalBodyTemplate()}   
            </section>
            <footer class="modal-card-foot is-justify-content-flex-end">
              <button class="button" id="cancel">取消</button>
              <button class="button is-success" id="ok">确定</button>
            </footer>
          </div>
        </div>
        `
    }

    _getModalBodyTemplate() {
        return ""
    }
}

class GeneralSettingsDialog extends SettingsDialog {
    constructor(settings) {
        super()
        this._settings = settings
    }

    _bindDialogEvents() {
        this._fontColorInput = this._dialog.querySelector("#font-color")
        this._fontSizeInput = this._dialog.querySelector("#font-size")
        this._pathWidthInput = this._dialog.querySelector("#path-width")
        this._pathToleranceInput = this._dialog.querySelector("#path-tolerance")
        this._fontColorPicker = this._dialog.querySelector("#font-color-picker")

        this._fontColorPicker.addEventListener("change", () => {
            this._fontColorInput.value = this._fontColorPicker.value.substring(1)
        })

        // Intercept super's ok button click listener event.
        this._dialog.querySelector("#ok").addEventListener("click", (event) => {
            if (this._checkInputs()) {
                const newSettings = {
                    fontColor: "#" + this._fontColorInput.value,
                    fontSize: `${this._fontSizeInput.value}px`,
                    pathWidth: parseInt(this._pathWidthInput.value),
                    pathTolerance: parseFloat(this._pathToleranceInput.value),
                }

                this._callback(newSettings)
            } else {
                event.preventDefault()
                event.stopImmediatePropagation()
            }
        })

        super._bindDialogEvents()
    }

    _checkInputs() {
        if (!this._checkInputColorValue(this._fontColorInput)) {
            return false
        }

        if (!this._checkInputIntValue(this._fontSizeInput)) {
            return false
        }

        if (!this._checkInputIntValue(this._pathWidthInput)) {
            return false
        }

        if (!this._checkInputFloatValue(this._pathToleranceInput)) {
            return false
        }

        return true
    }

    get fontColor() {
        return this._settings["fontColor"]
    }

    get fontSize() {
        return parseInt(this._settings["fontSize"])
    }

    get pathWidth() {
        return this._settings["pathWidth"]
    }

    get pathTolerance() {
        return this._settings["pathTolerance"]
    }

    _getModalBodyTemplate() {
       return `
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">文字颜色</label>
            </div>
            <div class="field-body">
              <div class="field"><div class="control"><input class="input" type="text" id="font-color" value="${this.fontColor.substring(1)}"></div></div>
              <div class="field"><div class="control"><input class="input" type="color" id="font-color-picker" value="${this.fontColor}"></div></div>
            </div>
          </div>
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">文字大小</label>
            </div>
            <div class="field-body">
              <div class="field"><div class="control"><input class="input" type="number" id="font-size" value="${this.fontSize}"></div></div>
            </div>
          </div>
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">路线宽度</label>
            </div>
            <div class="field-body">
              <div class="field"><div class="control"><input class="input" type="number" id="path-width" value="${this.pathWidth}"></div></div>
            </div>
          </div>
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">路线简化程度</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input class="input" type="number" step="0.001" min="0.001" max="1.0" id="path-tolerance" value="${this.pathTolerance}">
                  <span class="is-block my-1 is-size-7" style="color:#737373">
                    值范围为<b>0.001-1.0</b>. 值越小, 路线越接近原始路线, 值越大, 路线越简化, 默认值为<b>0.01</b>.
                  </span>
                </div>
              </div>
            </div>
          </div>
       `
    }
}

class PathSettingsDialog extends SettingsDialog {
    constructor(strokeColor) {
        super()
        this._strokeColor = strokeColor
    }

    _bindDialogEvents() {
        const pathColorInput = this._dialog.querySelector("#path-color")
        const pathColorPicker = this._dialog.querySelector("#path-color-picker")

        pathColorPicker.addEventListener("change", () => {
            pathColorInput.value = pathColorPicker.value.substring(1)
        })

        // Intercept super's ok button click listener event.
        this._dialog.querySelector("#ok").addEventListener("click", (event) => {
            if (this._checkInputColorValue(pathColorInput)) {
                this._callback("#" + pathColorInput.value)
            } else {
                event.preventDefault()
                event.stopImmediatePropagation()
            }
        })


        super._bindDialogEvents()
    }

    _getModalBodyTemplate() {
        return `
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">路线颜色</label>
            </div>
            <div class="field-body">
              <div class="field"><div class="control"><input class="input" type="text" id="path-color" value="${this._strokeColor.substring(1)}"></div></div>
              <div class="field"><div class="control"><input class="input" type="color" id="path-color-picker" value="${this._strokeColor}"></div></div>
            </div>
          </div>
       `
    }
}